import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import { 
  Users, Plus, Trash2, Edit2, UserPlus, UserMinus, RefreshCw, 
  ShieldCheck, UserCog, User, Pencil, Key, X, Shield, Lock,
  Eye, Settings, Search, Filter, FolderOpen, Check
} from 'lucide-react';

const ROLES = [
  { value: 'ANALISTA', label: 'Analista', icon: User },
  { value: 'SUPERVISOR', label: 'Supervisor', icon: UserCog },
  { value: 'ADMINISTRADOR', label: 'Administrador', icon: ShieldCheck },
];

const COLORES = [
  { value: '#ef4444', label: 'Rojo' },
  { value: '#f97316', label: 'Naranja' },
  { value: '#eab308', label: 'Amarillo' },
  { value: '#22c55e', label: 'Verde' },
  { value: '#3b82f6', label: 'Azul' },
  { value: '#8b5cf6', label: 'Púrpura' },
  { value: '#ec4899', label: 'Rosa' },
  { value: '#64748b', label: 'Gris' },
];

const getRoleColor = (rol) => {
  const colors = {
    ADMINISTRADOR: 'bg-purple-100 text-purple-900 font-medium',
    SUPERVISOR: 'bg-blue-100 text-blue-900 font-medium',
    ANALISTA: 'bg-slate-200 text-slate-800 font-medium',
  };
  return colors[rol] || colors.ANALISTA;
};

const GestionUsuarios = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('usuarios');
  
  // Users state
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [userSearch, setUserSearch] = useState('');
  const [userRoleFilter, setUserRoleFilter] = useState('');
  const [userGroupFilter, setUserGroupFilter] = useState('');
  
  // Groups state
  const [groups, setGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);
  
  // Modules for permissions
  const [modules, setModules] = useState([]);
  
  // Loading states
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Dialogs
  const [isCreateUserOpen, setIsCreateUserOpen] = useState(false);
  const [isEditUserOpen, setIsEditUserOpen] = useState(false);
  const [isPasswordDialogOpen, setIsPasswordDialogOpen] = useState(false);
  const [isCreateGroupOpen, setIsCreateGroupOpen] = useState(false);
  const [isEditGroupOpen, setIsEditGroupOpen] = useState(false);
  const [isAddMembersOpen, setIsAddMembersOpen] = useState(false);
  const [isPermissionsOpen, setIsPermissionsOpen] = useState(false);
  
  // Selected items
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedGroups, setSelectedGroups] = useState([]);
  
  // Forms
  const [userForm, setUserForm] = useState({ email: '', nombre: '', password: '', rol: 'ANALISTA', send_temp_password: false });
  const [passwordForm, setPasswordForm] = useState({ newPassword: '', confirmPassword: '' });
  const [groupForm, setGroupForm] = useState({ nombre: '', descripcion: '', color: '#3b82f6', permisos: [] });
  const [editingPermissions, setEditingPermissions] = useState({});

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    filterUsers();
  }, [users, userSearch, userRoleFilter, userGroupFilter]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [usersRes, groupsRes, modulesRes] = await Promise.all([
        api.get('/users'),
        api.get('/groups'),
        api.get('/modules')
      ]);
      setUsers(usersRes.data);
      setGroups(groupsRes.data);
      setModules(modulesRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const filterUsers = () => {
    let filtered = [...users];
    
    if (userSearch) {
      const search = userSearch.toLowerCase();
      filtered = filtered.filter(u => 
        u.email?.toLowerCase().includes(search) ||
        u.nombre?.toLowerCase().includes(search)
      );
    }
    
    if (userRoleFilter) {
      filtered = filtered.filter(u => u.rol === userRoleFilter);
    }
    
    if (userGroupFilter) {
      filtered = filtered.filter(u => 
        u.grupos?.some(g => g.id === userGroupFilter || g === userGroupFilter)
      );
    }
    
    setFilteredUsers(filtered);
  };

  // User CRUD
  const handleCreateUser = async () => {
    if (!userForm.email || !userForm.nombre) {
      toast.error('Complete todos los campos requeridos');
      return;
    }
    
    // Si no envía clave temporal, necesita password
    if (!userForm.send_temp_password && !userForm.password) {
      toast.error('Ingrese una contraseña o seleccione enviar clave temporal');
      return;
    }
    
    if (!userForm.send_temp_password && userForm.password.length < 6) {
      toast.error('La contraseña debe tener al menos 6 caracteres');
      return;
    }
    
    setSaving(true);
    try {
      await api.post('/users/', {
        ...userForm,
        grupos: selectedGroups
      });
      toast.success(userForm.send_temp_password 
        ? 'Usuario creado. Se envió la clave temporal por email.' 
        : 'Usuario creado exitosamente');
      setIsCreateUserOpen(false);
      setUserForm({ email: '', nombre: '', password: '', rol: 'ANALISTA', send_temp_password: false });
      setSelectedGroups([]);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear usuario');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateUser = async () => {
    if (!selectedUser) return;
    
    setSaving(true);
    try {
      await api.put(`/users/${selectedUser.id}`, {
        nombre: userForm.nombre,
        rol: userForm.rol,
        grupos: selectedGroups
      });
      toast.success('Usuario actualizado');
      setIsEditUserOpen(false);
      loadData();
    } catch (error) {
      toast.error('Error al actualizar usuario');
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async () => {
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      toast.error('Las contraseñas no coinciden');
      return;
    }
    if (passwordForm.newPassword.length < 6) {
      toast.error('La contraseña debe tener al menos 6 caracteres');
      return;
    }
    
    setSaving(true);
    try {
      await api.patch(`/users/${selectedUser.id}/password`, {
        new_password: passwordForm.newPassword
      });
      toast.success('Contraseña actualizada');
      setIsPasswordDialogOpen(false);
      setPasswordForm({ newPassword: '', confirmPassword: '' });
    } catch (error) {
      if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error('Error al cambiar contraseña');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteUser = async (user) => {
    if (!window.confirm(`¿Eliminar al usuario ${user.nombre}?`)) return;
    
    try {
      await api.delete(`/users/${user.id}`);
      toast.success('Usuario eliminado');
      loadData();
    } catch (error) {
      toast.error('Error al eliminar usuario');
    }
  };

  // Group CRUD
  const handleCreateGroup = async () => {
    if (!groupForm.nombre) {
      toast.error('El nombre es requerido');
      return;
    }
    
    setSaving(true);
    try {
      await api.post('/groups/', groupForm);
      toast.success('Grupo creado');
      setIsCreateGroupOpen(false);
      setGroupForm({ nombre: '', descripcion: '', color: '#3b82f6', permisos: [] });
      loadData();
    } catch (error) {
      toast.error('Error al crear grupo');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateGroup = async () => {
    if (!selectedGroup) return;
    
    setSaving(true);
    try {
      await api.put(`/groups/${selectedGroup.id}`, groupForm);
      toast.success('Grupo actualizado');
      setIsEditGroupOpen(false);
      loadData();
    } catch (error) {
      toast.error('Error al actualizar grupo');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteGroup = async (group) => {
    if (!window.confirm(`¿Eliminar el grupo ${group.nombre}?`)) return;
    
    try {
      await api.delete(`/groups/${group.id}`);
      toast.success('Grupo eliminado');
      loadData();
    } catch (error) {
      toast.error('Error al eliminar grupo');
    }
  };

  const handleSavePermissions = async () => {
    if (!selectedGroup) return;
    
    setSaving(true);
    try {
      await api.put(`/groups/${selectedGroup.id}/permissions`, {
        permisos: editingPermissions
      });
      toast.success('Permisos actualizados');
      setIsPermissionsOpen(false);
      loadData();
    } catch (error) {
      toast.error('Error al actualizar permisos');
    } finally {
      setSaving(false);
    }
  };

  // Members management
  const openManageMembers = (group) => {
    setSelectedGroup(group);
    setIsAddMembersOpen(true);
  };

  const handleAddMember = async (userId) => {
    if (!selectedGroup) return;
    
    try {
      await api.post(`/groups/${selectedGroup.id}/members`, { user_ids: [userId] });
      toast.success('Usuario agregado al grupo');
      loadData();
      // Actualizar el grupo seleccionado con los nuevos miembros
      const updatedGroups = await api.get('/groups');
      const updatedGroup = updatedGroups.data.find(g => g.id === selectedGroup.id);
      if (updatedGroup) setSelectedGroup(updatedGroup);
    } catch (error) {
      toast.error('Error al agregar usuario');
    }
  };

  const handleRemoveMember = async (userId) => {
    if (!selectedGroup) return;
    
    try {
      await api.delete(`/groups/${selectedGroup.id}/members/${userId}`);
      toast.success('Usuario removido del grupo');
      loadData();
      // Actualizar el grupo seleccionado
      const updatedGroups = await api.get('/groups');
      const updatedGroup = updatedGroups.data.find(g => g.id === selectedGroup.id);
      if (updatedGroup) setSelectedGroup(updatedGroup);
    } catch (error) {
      toast.error('Error al remover usuario');
    }
  };

  const openEditUser = (user) => {
    setSelectedUser(user);
    setUserForm({
      email: user.email,
      nombre: user.nombre,
      rol: user.rol,
      password: '',
      send_temp_password: false
    });
    setSelectedGroups(user.grupos?.map(g => g.id || g) || []);
    setIsEditUserOpen(true);
  };

  const openEditGroup = (group) => {
    setSelectedGroup(group);
    setGroupForm({
      nombre: group.nombre,
      descripcion: group.descripcion || '',
      color: group.color || '#3b82f6',
      permisos: group.permisos || []
    });
    setIsEditGroupOpen(true);
  };

  const openPermissions = (group) => {
    setSelectedGroup(group);
    // Convertir permisos al formato nuevo si están en formato antiguo
    const rawPermisos = group.permisos || {};
    const normalizedPermisos = {};
    
    for (const [moduleId, perm] of Object.entries(rawPermisos)) {
      if (Array.isArray(perm)) {
        // Formato nuevo: ya es una lista
        normalizedPermisos[moduleId] = perm;
      } else if (typeof perm === 'object') {
        // Formato antiguo: convertir
        const actions = [];
        if (perm.puede_ver) actions.push('ver');
        if (perm.puede_editar) {
          actions.push('crear', 'editar', 'eliminar');
        }
        normalizedPermisos[moduleId] = actions;
      }
    }
    
    setEditingPermissions(normalizedPermisos);
    setIsPermissionsOpen(true);
  };

  const togglePermission = (moduleId, action) => {
    setEditingPermissions(prev => {
      const modulePerms = prev[moduleId] || [];
      if (modulePerms.includes(action)) {
        return { ...prev, [moduleId]: modulePerms.filter(a => a !== action) };
      } else {
        return { ...prev, [moduleId]: [...modulePerms, action] };
      }
    });
  };

  // Stats
  const userStats = {
    total: users.length,
    admins: users.filter(u => u.rol === 'ADMINISTRADOR').length,
    supervisors: users.filter(u => u.rol === 'SUPERVISOR').length,
    analysts: users.filter(u => u.rol === 'ANALISTA').length,
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center">
        <RefreshCw className="animate-spin text-blue-600" size={32} />
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8 h-full overflow-auto bg-slate-50">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Users size={28} className="text-blue-600" />
            Gestión de Usuarios y Accesos
          </h1>
          <p className="text-slate-600 mt-1">Administrar usuarios, grupos y permisos del sistema</p>
        </div>
        <Button onClick={loadData} variant="outline" size="sm">
          <RefreshCw size={16} className="mr-2" />
          Actualizar
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Card className="p-4 bg-white">
          <div className="text-2xl font-bold text-slate-900">{userStats.total}</div>
          <div className="text-sm text-slate-600">Total Usuarios</div>
        </Card>
        <Card className="p-4 bg-purple-50 border-purple-200">
          <div className="text-2xl font-bold text-purple-700">{userStats.admins}</div>
          <div className="text-sm text-purple-600">Administradores</div>
        </Card>
        <Card className="p-4 bg-blue-50 border-blue-200">
          <div className="text-2xl font-bold text-blue-700">{userStats.supervisors}</div>
          <div className="text-sm text-blue-600">Supervisores</div>
        </Card>
        <Card className="p-4 bg-slate-100">
          <div className="text-2xl font-bold text-slate-700">{userStats.analysts}</div>
          <div className="text-sm text-slate-600">Analistas</div>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="bg-white border p-1">
          <TabsTrigger 
            value="usuarios" 
            className="px-4 py-2"
            style={{ 
              backgroundColor: activeTab === 'usuarios' ? '#dbeafe' : 'transparent',
              color: activeTab === 'usuarios' ? '#1e40af' : '#475569'
            }}
          >
            <User size={16} className="mr-2" />
            Usuarios ({users.length})
          </TabsTrigger>
          <TabsTrigger 
            value="grupos" 
            className="px-4 py-2"
            style={{ 
              backgroundColor: activeTab === 'grupos' ? '#dcfce7' : 'transparent',
              color: activeTab === 'grupos' ? '#166534' : '#475569'
            }}
          >
            <FolderOpen size={16} className="mr-2" />
            Grupos ({groups.length})
          </TabsTrigger>
          <TabsTrigger 
            value="permisos" 
            className="px-4 py-2"
            style={{ 
              backgroundColor: activeTab === 'permisos' ? '#fef3c7' : 'transparent',
              color: activeTab === 'permisos' ? '#b45309' : '#475569'
            }}
          >
            <Shield size={16} className="mr-2" />
            Control de Acceso
          </TabsTrigger>
        </TabsList>

        {/* Tab: Usuarios */}
        <TabsContent value="usuarios">
          <Card className="p-6 bg-white">
            {/* Filters */}
            <div className="flex flex-wrap gap-4 mb-6">
              <div className="flex-1 min-w-[200px]">
                <div className="relative">
                  <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input
                    placeholder="Buscar por nombre o email..."
                    value={userSearch}
                    onChange={(e) => setUserSearch(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>
              <Select value={userRoleFilter || "all"} onValueChange={(v) => setUserRoleFilter(v === "all" ? "" : v)}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Filtrar por rol" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los roles</SelectItem>
                  {ROLES.map(r => (
                    <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={userGroupFilter || "all"} onValueChange={(v) => setUserGroupFilter(v === "all" ? "" : v)}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Filtrar por grupo" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los grupos</SelectItem>
                  {groups.map(g => (
                    <SelectItem key={g.id} value={g.id}>{g.nombre}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button onClick={() => setIsCreateUserOpen(true)} style={{ backgroundColor: '#2563eb', color: 'white' }}>
                <Plus size={16} className="mr-2" />
                Nuevo Usuario
              </Button>
            </div>

            {/* Users Table */}
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Usuario</TableHead>
                  <TableHead>Rol</TableHead>
                  <TableHead>Grupos</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead className="text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredUsers.map(user => (
                  <TableRow key={user.id}>
                    <TableCell>
                      <div>
                        <p className="font-medium text-slate-900">{user.nombre}</p>
                        <p className="text-sm text-slate-500">{user.email}</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={getRoleColor(user.rol)}>{user.rol}</Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {user.grupos?.slice(0, 2).map((g) => (
                          <Badge key={g.id || g.nombre || g} variant="outline" className="text-xs">
                            {g.nombre || g}
                          </Badge>
                        ))}
                        {user.grupos?.length > 2 && (
                          <Badge variant="outline" className="text-xs">
                            +{user.grupos.length - 2}
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={user.activo !== false ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>
                        {user.activo !== false ? 'Activo' : 'Inactivo'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button 
                          size="sm" 
                          variant="outline" 
                          onClick={() => openEditUser(user)}
                          style={{ color: '#1d4ed8', borderColor: '#93c5fd' }}
                        >
                          <Pencil size={14} className="mr-1" />
                          Editar
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline" 
                          onClick={() => { setSelectedUser(user); setIsPasswordDialogOpen(true); }}
                          style={{ color: '#b45309', borderColor: '#fcd34d' }}
                        >
                          <Key size={14} className="mr-1" />
                          Clave
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline" 
                          style={{ color: '#b91c1c', borderColor: '#fca5a5' }}
                          onClick={() => handleDeleteUser(user)}
                        >
                          <Trash2 size={14} />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        {/* Tab: Grupos */}
        <TabsContent value="grupos">
          <Card className="p-6 bg-white">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-lg font-semibold text-slate-900">Grupos de Usuarios</h2>
              <Button onClick={() => setIsCreateGroupOpen(true)} style={{ backgroundColor: '#22c55e', color: 'white' }}>
                <Plus size={16} className="mr-2" />
                Nuevo Grupo
              </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {groups.map(group => (
                <Card key={group.id} className="p-4 border-l-4" style={{ borderLeftColor: group.color || '#3b82f6' }}>
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <h3 className="font-semibold text-slate-900">{group.nombre}</h3>
                      <p className="text-sm text-slate-500">{group.descripcion || 'Sin descripción'}</p>
                    </div>
                    <Badge className="bg-slate-100 text-slate-700">
                      {group.miembros?.length || 0} miembros
                    </Badge>
                  </div>
                  
                  {/* Miembros preview */}
                  {group.miembros?.length > 0 && (
                    <div className="mb-3">
                      <p className="text-xs text-slate-500 mb-1">Miembros:</p>
                      <div className="flex flex-wrap gap-1">
                        {group.miembros.slice(0, 3).map((m) => (
                          <Badge key={m.id || m.nombre || m} variant="outline" className="text-xs">
                            {m.nombre || m}
                          </Badge>
                        ))}
                        {group.miembros.length > 3 && (
                          <Badge variant="outline" className="text-xs">
                            +{group.miembros.length - 3}
                          </Badge>
                        )}
                      </div>
                    </div>
                  )}

                  <div className="flex justify-end gap-1 pt-2 border-t">
                    <Button size="sm" variant="ghost" onClick={() => openManageMembers(group)} title="Gestionar Miembros">
                      <UserPlus size={14} />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => openPermissions(group)} title="Permisos">
                      <Shield size={14} />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => openEditGroup(group)} title="Editar">
                      <Pencil size={14} />
                    </Button>
                    <Button size="sm" variant="ghost" className="text-red-600" onClick={() => handleDeleteGroup(group)} title="Eliminar">
                      <Trash2 size={14} />
                    </Button>
                  </div>
                </Card>
              ))}
            </div>
          </Card>
        </TabsContent>

        {/* Tab: Control de Acceso */}
        <TabsContent value="permisos">
          <Card className="p-6 bg-white">
            {/* Banner de nuevo sistema */}
            <div className="mb-6 p-4 bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200 rounded-lg">
              <div className="flex items-start gap-3">
                <Shield className="text-purple-600 mt-0.5" size={20} />
                <div className="flex-1">
                  <h3 className="font-semibold text-purple-900">Nuevo: Control de Acceso Granular</h3>
                  <p className="text-sm text-purple-700 mt-1">
                    Ahora puedes configurar permisos más detallados por sección y definir el alcance de datos (propio, grupo, todos).
                  </p>
                  <Button
                    size="sm"
                    className="mt-3 bg-purple-600 hover:bg-purple-700 text-white"
                    onClick={() => navigate('/m/configuracion/permisos-v2')}
                  >
                    <Settings size={14} className="mr-2" />
                    Ir a Control de Acceso Granular
                  </Button>
                </div>
              </div>
            </div>

            <div className="mb-6">
              <h2 className="text-lg font-semibold text-slate-900 mb-2">Control de Acceso por Módulo (Básico)</h2>
              <p className="text-sm text-slate-600">Permisos básicos por módulo. Para permisos granulares, usa el nuevo sistema.</p>
            </div>

            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Grupo</TableHead>
                  <TableHead>Módulos con Acceso</TableHead>
                  <TableHead>Miembros</TableHead>
                  <TableHead className="text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {groups.map(group => {
                  const permCount = Object.keys(group.permisos || {}).length;
                  return (
                    <TableRow key={group.id}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: group.color || '#3b82f6' }} />
                          <span className="font-medium">{group.nombre}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={permCount > 0 ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600'}>
                          {permCount} módulos
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <span className="text-slate-600">{group.miembros?.length || 0} usuarios</span>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => openPermissions(group)}
                          style={{ color: '#b45309', borderColor: '#fcd34d' }}
                        >
                          <Settings size={14} className="mr-1" />
                          Configurar
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Dialog: Create User */}
      <Dialog open={isCreateUserOpen} onOpenChange={setIsCreateUserOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Crear Usuario</DialogTitle>
            <DialogDescription>Ingrese los datos del nuevo usuario</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Email *</Label>
              <Input
                type="email"
                value={userForm.email}
                onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
                placeholder="usuario@empresa.com"
              />
            </div>
            <div>
              <Label>Nombre *</Label>
              <Input
                value={userForm.nombre}
                onChange={(e) => setUserForm({ ...userForm, nombre: e.target.value })}
                placeholder="Nombre completo"
              />
            </div>
            
            {/* Método de Contraseña */}
            <div className="space-y-3">
              <Label>Método de Contraseña</Label>
              
              {/* Opción 1: Clave temporal */}
              <div
                onClick={() => setUserForm({ ...userForm, send_temp_password: true, password: '' })}
                className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                  userForm.send_temp_password 
                    ? 'border-blue-500 bg-blue-50' 
                    : 'border-slate-200 hover:border-slate-300'
                }`}
              >
                <div className="flex items-start gap-3">
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center mt-0.5 ${
                    userForm.send_temp_password ? 'border-blue-500' : 'border-slate-300'
                  }`}>
                    {userForm.send_temp_password && <div className="w-2.5 h-2.5 rounded-full bg-blue-500" />}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-slate-800">Enviar clave temporal por email</span>
                      <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">Recomendado</span>
                    </div>
                    <p className="text-sm text-slate-500 mt-1">
                      Se genera una contraseña segura automáticamente y se envía al email del usuario. 
                      Deberá cambiarla en su primer inicio de sesión.
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Opción 2: Contraseña manual */}
              <div
                onClick={() => setUserForm({ ...userForm, send_temp_password: false })}
                className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                  !userForm.send_temp_password 
                    ? 'border-blue-500 bg-blue-50' 
                    : 'border-slate-200 hover:border-slate-300'
                }`}
              >
                <div className="flex items-start gap-3">
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center mt-0.5 ${
                    !userForm.send_temp_password ? 'border-blue-500' : 'border-slate-300'
                  }`}>
                    {!userForm.send_temp_password && <div className="w-2.5 h-2.5 rounded-full bg-blue-500" />}
                  </div>
                  <div className="flex-1">
                    <span className="font-medium text-slate-800">Definir contraseña manualmente</span>
                    <p className="text-sm text-slate-500 mt-1">
                      Vos definís la contraseña. Deberás comunicársela al usuario por otro medio.
                    </p>
                    {!userForm.send_temp_password && (
                      <Input
                        className="mt-3"
                        type="password"
                        value={userForm.password}
                        onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
                        placeholder="Mínimo 6 caracteres"
                        onClick={(e) => e.stopPropagation()}
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
            
            <div>
              <Label>Rol</Label>
              <Select value={userForm.rol} onValueChange={(v) => setUserForm({ ...userForm, rol: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ROLES.map(r => (
                    <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Grupos</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {groups.map(g => (
                  <Badge
                    key={g.id}
                    variant={selectedGroups.includes(g.id) ? 'default' : 'outline'}
                    className="cursor-pointer"
                    onClick={() => {
                      setSelectedGroups(prev => 
                        prev.includes(g.id) 
                          ? prev.filter(id => id !== g.id)
                          : [...prev, g.id]
                      );
                    }}
                  >
                    {g.nombre}
                  </Badge>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCreateUserOpen(false)}>Cancelar</Button>
            <Button onClick={handleCreateUser} disabled={saving} style={{ backgroundColor: '#2563eb', color: 'white' }}>
              {saving ? 'Creando...' : 'Crear Usuario'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Edit User */}
      <Dialog open={isEditUserOpen} onOpenChange={setIsEditUserOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Editar Usuario</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Email</Label>
              <Input value={userForm.email} disabled className="bg-slate-50" />
            </div>
            <div>
              <Label>Nombre</Label>
              <Input
                value={userForm.nombre}
                onChange={(e) => setUserForm({ ...userForm, nombre: e.target.value })}
              />
            </div>
            <div>
              <Label>Rol</Label>
              <Select value={userForm.rol} onValueChange={(v) => setUserForm({ ...userForm, rol: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ROLES.map(r => (
                    <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Grupos</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {groups.map(g => (
                  <Badge
                    key={g.id}
                    variant={selectedGroups.includes(g.id) ? 'default' : 'outline'}
                    className="cursor-pointer"
                    onClick={() => {
                      setSelectedGroups(prev => 
                        prev.includes(g.id) 
                          ? prev.filter(id => id !== g.id)
                          : [...prev, g.id]
                      );
                    }}
                  >
                    {g.nombre}
                  </Badge>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditUserOpen(false)}>Cancelar</Button>
            <Button onClick={handleUpdateUser} disabled={saving} style={{ backgroundColor: '#2563eb', color: 'white' }}>
              {saving ? 'Guardando...' : 'Guardar Cambios'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Change Password */}
      <Dialog open={isPasswordDialogOpen} onOpenChange={setIsPasswordDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cambiar Contraseña</DialogTitle>
            <DialogDescription>
              Cambiar contraseña para: {selectedUser?.nombre}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Nueva Contraseña</Label>
              <Input
                type="password"
                value={passwordForm.newPassword}
                onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
              />
            </div>
            <div>
              <Label>Confirmar Contraseña</Label>
              <Input
                type="password"
                value={passwordForm.confirmPassword}
                onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsPasswordDialogOpen(false)}>Cancelar</Button>
            <Button onClick={handleChangePassword} disabled={saving} style={{ backgroundColor: '#dc2626', color: 'white' }}>
              {saving ? 'Cambiando...' : 'Cambiar Contraseña'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Create Group */}
      <Dialog open={isCreateGroupOpen} onOpenChange={setIsCreateGroupOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Crear Grupo</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Nombre *</Label>
              <Input
                value={groupForm.nombre}
                onChange={(e) => setGroupForm({ ...groupForm, nombre: e.target.value })}
                placeholder="Nombre del grupo"
              />
            </div>
            <div>
              <Label>Descripción</Label>
              <Textarea
                value={groupForm.descripcion}
                onChange={(e) => setGroupForm({ ...groupForm, descripcion: e.target.value })}
                placeholder="Descripción del grupo"
              />
            </div>
            <div>
              <Label>Color</Label>
              <div className="flex gap-2 mt-2">
                {COLORES.map(c => (
                  <button
                    key={c.value}
                    className={`w-8 h-8 rounded-full border-2 ${groupForm.color === c.value ? 'border-slate-900' : 'border-transparent'}`}
                    style={{ backgroundColor: c.value }}
                    onClick={() => setGroupForm({ ...groupForm, color: c.value })}
                    title={c.label}
                  />
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCreateGroupOpen(false)}>Cancelar</Button>
            <Button onClick={handleCreateGroup} disabled={saving} style={{ backgroundColor: '#22c55e', color: 'white' }}>
              {saving ? 'Creando...' : 'Crear Grupo'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Edit Group */}
      <Dialog open={isEditGroupOpen} onOpenChange={setIsEditGroupOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Editar Grupo</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Nombre</Label>
              <Input
                value={groupForm.nombre}
                onChange={(e) => setGroupForm({ ...groupForm, nombre: e.target.value })}
              />
            </div>
            <div>
              <Label>Descripción</Label>
              <Textarea
                value={groupForm.descripcion}
                onChange={(e) => setGroupForm({ ...groupForm, descripcion: e.target.value })}
              />
            </div>
            <div>
              <Label>Color</Label>
              <div className="flex gap-2 mt-2">
                {COLORES.map(c => (
                  <button
                    key={c.value}
                    className={`w-8 h-8 rounded-full border-2 ${groupForm.color === c.value ? 'border-slate-900' : 'border-transparent'}`}
                    style={{ backgroundColor: c.value }}
                    onClick={() => setGroupForm({ ...groupForm, color: c.value })}
                    title={c.label}
                  />
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditGroupOpen(false)}>Cancelar</Button>
            <Button onClick={handleUpdateGroup} disabled={saving} style={{ backgroundColor: '#22c55e', color: 'white' }}>
              {saving ? 'Guardando...' : 'Guardar Cambios'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Permissions - Mejorado con Secciones */}
      <Dialog open={isPermissionsOpen} onOpenChange={setIsPermissionsOpen}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="text-amber-600" size={20} />
              Permisos: {selectedGroup?.nombre}
            </DialogTitle>
            <DialogDescription>
              Configure el acceso a los módulos y secciones del sistema. Si un módulo no tiene "Ver" marcado, el usuario no lo verá en el menú.
            </DialogDescription>
          </DialogHeader>
          
          {/* Leyenda de permisos */}
          <div className="bg-slate-50 rounded-lg p-3 text-xs grid grid-cols-2 md:grid-cols-3 gap-2">
            <div className="flex items-center gap-2">
              <Eye size={14} className="text-blue-600" />
              <span><strong>Ver:</strong> Visualizar datos</span>
            </div>
            <div className="flex items-center gap-2">
              <Plus size={14} className="text-green-600" />
              <span><strong>Crear:</strong> Crear registros</span>
            </div>
            <div className="flex items-center gap-2">
              <Edit2 size={14} className="text-amber-600" />
              <span><strong>Editar:</strong> Modificar datos</span>
            </div>
            <div className="flex items-center gap-2">
              <Trash2 size={14} className="text-red-600" />
              <span><strong>Eliminar:</strong> Borrar registros</span>
            </div>
            <div className="flex items-center gap-2">
              <RefreshCw size={14} className="text-purple-600" />
              <span><strong>Ejecutar:</strong> Ejecutar acciones</span>
            </div>
            <div className="flex items-center gap-2">
              <Check size={14} className="text-indigo-600" />
              <span><strong>Aprobar:</strong> Aprobar/rechazar</span>
            </div>
          </div>
          
          {/* Acciones rápidas */}
          <div className="flex gap-2 mt-2">
            <Button 
              size="sm" 
              variant="outline" 
              onClick={() => {
                const newPerms = {};
                modules.forEach(m => {
                  newPerms[m.id] = ['ver', 'crear', 'editar', 'eliminar', 'ejecutar', 'aprobar'];
                });
                setEditingPermissions(newPerms);
              }}
            >
              Seleccionar Todo
            </Button>
            <Button 
              size="sm" 
              variant="outline" 
              onClick={() => {
                const newPerms = {};
                modules.forEach(m => {
                  newPerms[m.id] = ['ver'];
                });
                setEditingPermissions(newPerms);
              }}
            >
              Solo Lectura
            </Button>
            <Button 
              size="sm" 
              variant="outline" 
              onClick={() => setEditingPermissions({})}
            >
              Quitar Todo
            </Button>
          </div>

          <div className="flex-1 overflow-y-auto mt-4 border rounded-lg">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-100">
                  <TableHead className="w-[250px] sticky left-0 bg-slate-100">Módulo / Sección</TableHead>
                  <TableHead className="text-center w-[70px]">
                    <div className="flex flex-col items-center">
                      <Eye size={14} className="text-blue-600" />
                      <span className="text-xs">Ver</span>
                    </div>
                  </TableHead>
                  <TableHead className="text-center w-[70px]">
                    <div className="flex flex-col items-center">
                      <Plus size={14} className="text-green-600" />
                      <span className="text-xs">Crear</span>
                    </div>
                  </TableHead>
                  <TableHead className="text-center w-[70px]">
                    <div className="flex flex-col items-center">
                      <Edit2 size={14} className="text-amber-600" />
                      <span className="text-xs">Editar</span>
                    </div>
                  </TableHead>
                  <TableHead className="text-center w-[70px]">
                    <div className="flex flex-col items-center">
                      <Trash2 size={14} className="text-red-600" />
                      <span className="text-xs">Eliminar</span>
                    </div>
                  </TableHead>
                  <TableHead className="text-center w-[70px]">
                    <div className="flex flex-col items-center">
                      <RefreshCw size={14} className="text-purple-600" />
                      <span className="text-xs">Ejecutar</span>
                    </div>
                  </TableHead>
                  <TableHead className="text-center w-[70px]">
                    <div className="flex flex-col items-center">
                      <Check size={14} className="text-indigo-600" />
                      <span className="text-xs">Aprobar</span>
                    </div>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {modules.map(module => {
                  const perms = editingPermissions[module.id] || [];
                  const hasAnyPerm = perms.length > 0;
                  return (
                    <React.Fragment key={module.id}>
                      {/* Fila del módulo */}
                      <TableRow className={`hover:bg-slate-50 ${hasAnyPerm ? 'bg-green-50' : ''}`}>
                        <TableCell className="font-semibold sticky left-0 bg-inherit">
                          <div className="flex items-center gap-2">
                            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: module.color || '#6b7280' }} />
                            {module.nombre}
                            {hasAnyPerm && (
                              <Badge className="bg-green-100 text-green-700 text-xs ml-2">
                                {perms.length} permisos
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                        {['ver', 'crear', 'editar', 'eliminar', 'ejecutar', 'aprobar'].map(action => (
                          <TableCell key={action} className="text-center">
                            <Checkbox
                              checked={perms.includes(action)}
                              onCheckedChange={() => togglePermission(module.id, action)}
                            />
                          </TableCell>
                        ))}
                      </TableRow>
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </div>
          
          {/* Info importante */}
          <div className="mt-4 p-3 bg-amber-50 rounded-lg text-xs text-amber-800 border border-amber-200">
            <strong>⚠️ Importante:</strong> Los módulos sin permiso "Ver" no aparecerán en el menú del usuario. Los usuarios del grupo heredan todos los permisos configurados aquí.
          </div>
          
          <DialogFooter className="mt-4 border-t pt-4">
            <Button variant="outline" onClick={() => setIsPermissionsOpen(false)}>Cancelar</Button>
            <Button 
              onClick={handleSavePermissions} 
              disabled={saving} 
              style={{ backgroundColor: '#b45309', color: 'white' }}
              data-testid="save-permissions-btn"
            >
              {saving ? 'Guardando...' : 'Guardar Permisos'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Gestionar Miembros del Grupo */}
      <Dialog open={isAddMembersOpen} onOpenChange={setIsAddMembersOpen}>
        <DialogContent className="max-w-2xl bg-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="text-green-600" size={20} />
              Miembros: {selectedGroup?.nombre}
            </DialogTitle>
            <DialogDescription>
              Agregue o remueva usuarios de este grupo
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid grid-cols-2 gap-4 mt-4">
            {/* Miembros actuales */}
            <div>
              <h4 className="font-medium text-slate-700 mb-2 flex items-center gap-2">
                <UserMinus size={16} />
                Miembros Actuales ({selectedGroup?.miembros?.length || 0})
              </h4>
              <div className="border rounded-lg max-h-[300px] overflow-y-auto">
                {selectedGroup?.miembros?.length === 0 ? (
                  <p className="text-sm text-slate-500 p-4 text-center">Sin miembros</p>
                ) : (
                  selectedGroup?.miembros?.map(member => (
                    <div key={member.id} className="flex items-center justify-between p-2 hover:bg-slate-50 border-b last:border-b-0">
                      <div>
                        <p className="text-sm font-medium">{member.nombre}</p>
                        <p className="text-xs text-slate-500">{member.email}</p>
                      </div>
                      <Button 
                        size="sm" 
                        variant="ghost" 
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        onClick={() => handleRemoveMember(member.id)}
                      >
                        <X size={14} />
                      </Button>
                    </div>
                  ))
                )}
              </div>
            </div>
            
            {/* Usuarios disponibles */}
            <div>
              <h4 className="font-medium text-slate-700 mb-2 flex items-center gap-2">
                <UserPlus size={16} />
                Usuarios Disponibles
              </h4>
              <div className="border rounded-lg max-h-[300px] overflow-y-auto">
                {users
                  .filter(u => u.activo !== false && !selectedGroup?.miembros?.some(m => m.id === u.id))
                  .map(user => (
                    <div key={user.id} className="flex items-center justify-between p-2 hover:bg-slate-50 border-b last:border-b-0">
                      <div>
                        <p className="text-sm font-medium">{user.nombre}</p>
                        <p className="text-xs text-slate-500">{user.email}</p>
                      </div>
                      <Button 
                        size="sm" 
                        variant="ghost" 
                        className="text-green-600 hover:text-green-700 hover:bg-green-50"
                        onClick={() => handleAddMember(user.id)}
                      >
                        <Plus size={14} />
                      </Button>
                    </div>
                  ))
                }
                {users.filter(u => u.activo !== false && !selectedGroup?.miembros?.some(m => m.id === u.id)).length === 0 && (
                  <p className="text-sm text-slate-500 p-4 text-center">Todos los usuarios ya son miembros</p>
                )}
              </div>
            </div>
          </div>
          
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setIsAddMembersOpen(false)}>Cerrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GestionUsuarios;
